/* ──────────────────────────────────────────────────────────────
   Settings — "Coming soon" preview.
   NOTE: The full Settings implementation is preserved (commented
   out) below this component. Nothing was removed — to restore the
   old screen, delete this component and uncomment the block below.
   ────────────────────────────────────────────────────────────── */

import React from 'react';
import { View, Text, ScrollView, Platform, ViewStyle } from 'react-native';
import { useTheme } from '../context/themeContext';
import { Icon } from '../components/ui';

const FEATURES = [
  'A personalised workspace, branded to feel like yours',
  'Team roles & permissions that scale with you',
  'Smart preferences that adapt to how you work',
];

export default function SettingsScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows, font } = theme;

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.paper }}
      contentContainerStyle={{ padding: 24, paddingBottom: 64, flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}
    >
      <View
        style={{
          width: '100%',
          maxWidth: 560,
          backgroundColor: colors.card,
          borderWidth: 1,
          borderColor: colors.line,
          borderRadius: radii.r4,
          paddingVertical: 40,
          paddingHorizontal: 32,
          alignItems: 'center',
          overflow: 'hidden',
          ...(shadows.lg as ViewStyle),
        }}
      >
        {/* ambient glow */}
        <View
          // @ts-ignore web radial glow
          style={Platform.OS === 'web'
            ? ({ position: 'absolute', top: -70, width: 300, height: 220, borderRadius: 150, background: `radial-gradient(circle, ${colors.accent}26, transparent 70%)` } as any)
            : { position: 'absolute', top: -70, width: 300, height: 220, borderRadius: 150, backgroundColor: colors.accentSoft }}
        />

        {/* icon ring */}
        <View
          // @ts-ignore web gradient chip
          style={{
            width: 68, height: 68, borderRadius: 20, alignItems: 'center', justifyContent: 'center',
            borderWidth: 1, borderColor: colors.accentLine, marginBottom: 18,
            ...Platform.select({ web: { background: `linear-gradient(135deg, ${colors.accent}33, ${colors.accent}14)` } as any, default: { backgroundColor: colors.accentSoft } }),
          }}
        >
          <Icon name="sparkles" size={28} color={colors.accent} />
        </View>

        {/* coming-soon badge */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 5, paddingHorizontal: 12, borderRadius: 999, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, marginBottom: 16 }}>
          <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.accent }} />
          <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.accentInk, fontFamily: font.sans, letterSpacing: 0.5 }}>COMING SOON</Text>
        </View>

        <Text style={{ fontSize: fontSize['2xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans, textAlign: 'center', letterSpacing: -0.4 }}>
          Settings, reimagined ✨
        </Text>
        <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: font.sans, textAlign: 'center', marginTop: 10, lineHeight: 22, maxWidth: 430 }}>
          We're crafting a beautiful, powerful Settings experience to put you fully in control of your workspace. It's almost here — and we think you're going to love it.
        </Text>

        <View style={{ gap: 12, marginTop: 24, maxWidth: 380, width: '100%' }}>
          {FEATURES.map((f) => (
            <View key={f} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Icon name="check-circle" size={16} color={colors.success} />
              <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>{f}</Text>
            </View>
          ))}
        </View>

        <Text style={{ fontSize: fontSize.xs, color: colors.ink4, fontFamily: font.sans, textAlign: 'center', marginTop: 26 }}>
          Thank you for growing with SK Catalog — the best is yet to come. 💜
        </Text>
      </View>
    </ScrollView>
  );
}


// ════════════════════════════════════════════════════════════════
// ORIGINAL SETTINGS IMPLEMENTATION — preserved below, fully commented out.
// Nothing was removed. To restore: delete the preview component above
// and uncomment this block.
// ════════════════════════════════════════════════════════════════

// /* ──────────────────────────────────────────────────────────────
//    Settings/index.tsx · Roles, Languages, Compliance
//    ────────────────────────────────────────────────────────────── */
// 
// import React, { useState } from 'react';
// import {
//   View,
//   Text,
//   ScrollView,
//   TouchableOpacity,
//   useWindowDimensions,
//   ViewStyle,
//   TextStyle,
// } from 'react-native';
// import { useTheme } from '../context/themeContext';
// import { SK } from '../data/mockData';
// import {
//   Icon,
//   Button,
//   Chip,
//   Input,
//   Select,
//   Switch,
//   Card,
//   Alert,
//   Modal,
//   Tabs,
//   Avatar,
//   Popover,
//   MenuItem,
//   MenuSeparator,
//   useToast,
// } from '../components/ui';
// 
// // ── InviteMemberModal ────────────────────────────────────────
// 
// interface InviteMemberModalProps {
//   open: boolean;
//   onClose: () => void;
// }
// 
// function InviteMemberModal({ open, onClose }: InviteMemberModalProps) {
//   const { theme } = useTheme();
//   const { colors } = theme;
//   const toast = useToast();
// 
//   const handleSend = () => {
//     onClose();
//     toast.success('Invitation sent');
//   };
// 
//   return (
//     <Modal
//       open={open}
//       onClose={onClose}
//       title="Invite team member"
//       footer={
//         <>
//           <Button onPress={onClose}>Cancel</Button>
//           <Button
//             kind="accent"
//             icon={<Icon name="send" size={15} color={colors.white} />}
//             onPress={handleSend}
//           >
//             Send invite
//           </Button>
//         </>
//       }
//     >
//       <View style={{ gap: 14 }}>
//         <Input
//           label="Email address"
//           keyboardType="email-address"
//           placeholder="name@company.com"
//           autoFocus
//         />
//         <Select
//           label="Role"
//           options={['Editor', 'Admin', 'Viewer'].map(r => ({ label: r, value: r }))}
//         />
//         <Alert tone="info" icon="shield">
//           Editors can manage content but not schema or distribution.
//         </Alert>
//       </View>
//     </Modal>
//   );
// }
// 
// // ── Tab: Roles ───────────────────────────────────────────────
// 
// interface TabRolesProps {
//   onInvite: () => void;
// }
// 
// function TabRoles({ onInvite }: TabRolesProps) {
//   const { theme } = useTheme();
//   const { colors, fontSize, fontWeight, radii } = theme;
//   const toast = useToast();
//   const { width: winW } = useWindowDimensions();
//   const isMobile = winW < 820;
// 
//   const thStyle: TextStyle = {
//     fontSize: fontSize.xs,
//     fontWeight: fontWeight.medium,
//     color: colors.ink3,
//     fontFamily: theme.font.sans,
//     paddingVertical: 8,
//     paddingHorizontal: 12,
//     textAlign: 'left',
//   };
// 
//   return (
//     <View>
//       {/* t-spread header */}
//       <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
//         <Text
//           style={{
//             fontSize: fontSize.xl,
//             fontWeight: fontWeight.semibold,
//             letterSpacing: -0.01 * fontSize.xl,
//             color: colors.ink,
//             fontFamily: theme.font.sans,
//           }}
//         >
//           Team members
//         </Text>
//         <Button
//           kind="accent"
//           icon={<Icon name="plus" size={15} color={colors.white} />}
//           onPress={onInvite}
//         >
//           Invite member
//         </Button>
//       </View>
// 
//       {/* Users table */}
//       <View
//         style={{
//           backgroundColor: colors.card,
//           borderWidth: 1,
//           borderColor: colors.line,
//           borderRadius: radii.r3,
//           overflow: 'hidden',
//           ...(theme.shadows.sm as ViewStyle),
//           marginBottom: 28,
//         }}
//       >
//         <ScrollView horizontal={!isMobile} showsHorizontalScrollIndicator={false}>
//           <View style={{ minWidth: isMobile ? '100%' : 560, ...(isMobile ? { width: '100%' } : {}) }}>
//             {/* thead — desktop only */}
//             {!isMobile && (
//               <View
//                 style={{
//                   flexDirection: 'row',
//                   backgroundColor: colors.paper2,
//                   borderBottomWidth: 1,
//                   borderBottomColor: colors.line,
//                 }}
//               >
//                 {['MEMBER', 'ROLE', 'STATUS', ''].map((h, i) => (
//                   <View key={i} style={{ flex: i === 3 ? 0 : i === 0 ? 3 : 2, width: i === 3 ? 40 : undefined }}>
//                     <Text style={thStyle}>{h}</Text>
//                   </View>
//                 ))}
//               </View>
//             )}
// 
//             {/* tbody */}
//             {SK.users.map(u => {
//               const member = (
//                 <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
//                   <Avatar name={u.name} size="sm" />
//                   <View style={{ flex: 1, minWidth: 0 }}>
//                     <Text numberOfLines={1} style={{ fontWeight: fontWeight.medium, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans }}>{u.name}</Text>
//                     <Text numberOfLines={1} style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>{u.email}</Text>
//                   </View>
//                 </View>
//               );
//               const roleCtl = (
//                 <Popover
//                   align="left"
//                   width={150}
//                   trigger={
//                     <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: 6, height: 30, paddingHorizontal: 10, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, backgroundColor: colors.card, alignSelf: 'flex-start' }}>
//                       <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, fontFamily: theme.font.sans }}>{u.role}</Text>
//                       <Icon name="chev-d" size={12} color={colors.ink3} />
//                     </TouchableOpacity>
//                   }
//                 >
//                   {(close: () => void) =>
//                     ['Admin', 'Editor', 'Viewer'].map(r => (
//                       <MenuItem key={r} checked={r === u.role} onPress={() => { close(); toast.success(`${u.name} is now ${r}`); }}>{r}</MenuItem>
//                     ))
//                   }
//                 </Popover>
//               );
//               const statusChip = <Chip tone={u.status === 'active' ? 'success' : 'warn'} dot>{u.status}</Chip>;
//               const actions = (
//                 <Popover
//                   align="right"
//                   width={150}
//                   trigger={<Button kind="ghost" size="sm" iconOnly icon={<Icon name="more" size={15} color={colors.ink3} />} />}
//                 >
//                   {(close: () => void) => (
//                     <>
//                       <MenuItem icon="mail" onPress={() => { close(); toast.info('Invite resent'); }}>Resend invite</MenuItem>
//                       <MenuSeparator />
//                       <MenuItem icon="trash" danger onPress={() => { close(); toast.warn('Access removed'); }}>Remove</MenuItem>
//                     </>
//                   )}
//                 </Popover>
//               );
// 
//               // ── Mobile: stacked card ──
//               if (isMobile) {
//                 return (
//                   <View key={u.id} style={{ borderBottomWidth: 1, borderBottomColor: colors.line, padding: 12, gap: 10 }}>
//                     <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
//                       {member}
//                       {actions}
//                     </View>
//                     <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
//                       {roleCtl}
//                       {statusChip}
//                     </View>
//                   </View>
//                 );
//               }
// 
//               // ── Desktop: table row ──
//               return (
//                 <View key={u.id} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: colors.line, alignItems: 'center' }}>
//                   <View style={{ flex: 3, paddingVertical: 10, paddingHorizontal: 12 }}>{member}</View>
//                   <View style={{ flex: 2, paddingVertical: 10, paddingHorizontal: 12 }}>{roleCtl}</View>
//                   <View style={{ flex: 2, paddingVertical: 10, paddingHorizontal: 12 }}>{statusChip}</View>
//                   <View style={{ width: 40, paddingVertical: 10, paddingHorizontal: 6 }}>{actions}</View>
//                 </View>
//               );
//             })}
//           </View>
//         </ScrollView>
//       </View>
// 
//       {/* What each role can do */}
//       <Text
//         style={{
//           fontSize: fontSize.xl,
//           fontWeight: fontWeight.semibold,
//           letterSpacing: -0.01 * fontSize.xl,
//           color: colors.ink,
//           fontFamily: theme.font.sans,
//           marginBottom: 14,
//         }}
//       >
//         What each role can do
//       </Text>
// 
//       <View
//         style={{
//           flexDirection: 'row',
//           flexWrap: 'wrap',
//           gap: 14,
//         }}
//       >
//         {SK.roles.map(r => (
//           <View key={r.name} style={{ flex: 1, minWidth: 200 }}>
//             <Card title={r.name} sub={r.desc}>
//               <View style={{ gap: 7 }}>
//                 {r.perms.map(p => (
//                   <View key={p} style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
//                     <Icon name="check" size={14} color={colors.success} style={{ marginTop: 2, flexShrink: 0 } as any} />
//                     <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans, flex: 1 }}>
//                       {p}
//                     </Text>
//                   </View>
//                 ))}
//               </View>
//             </Card>
//           </View>
//         ))}
//       </View>
//     </View>
//   );
// }
// 
// // ── Tab: Languages ───────────────────────────────────────────
// 
// function TabLanguages() {
//   const { theme } = useTheme();
//   const { colors, fontSize, fontWeight, radii } = theme;
//   const toast = useToast();
//   const { width: winW } = useWindowDimensions();
//   const isMobile = winW < 820;
// 
//   const thStyle: TextStyle = {
//     fontSize: fontSize.xs,
//     fontWeight: fontWeight.medium,
//     color: colors.ink3,
//     fontFamily: theme.font.sans,
//     paddingVertical: 8,
//     paddingHorizontal: 12,
//     textAlign: 'left',
//   };
// 
//   const getCoverage = (l: { isDefault?: boolean; code: string }) => {
//     if (l.isDefault) return 100;
//     if (l.code === 'ta-IN') return 64;
//     return 38;
//   };
// 
//   return (
//     <View>
//       <Alert tone="info" icon="translate" title="Multi-language is foundational">
//         Translatable fields store a value per language. Missing translations fall back to the default locale — never to blank.
//       </Alert>
// 
//       <View
//         style={{
//           backgroundColor: colors.card,
//           borderWidth: 1,
//           borderColor: colors.line,
//           borderRadius: radii.r3,
//           overflow: 'hidden',
//           ...(theme.shadows.sm as ViewStyle),
//           marginTop: 16,
//         }}
//       >
//         <ScrollView horizontal={!isMobile} showsHorizontalScrollIndicator={false}>
//           <View style={{ minWidth: isMobile ? '100%' : 600, ...(isMobile ? { width: '100%' } : {}) }}>
//             {/* thead — desktop only */}
//             {!isMobile && (
//               <View
//                 style={{
//                   flexDirection: 'row',
//                   backgroundColor: colors.paper2,
//                   borderBottomWidth: 1,
//                   borderBottomColor: colors.line,
//                 }}
//               >
//                 {['Language', 'Code (BCP-47)', 'Coverage', 'Default', ''].map((h, i) => (
//                   <View key={i} style={{ flex: i === 4 ? 0 : 2, width: i === 4 ? 40 : undefined }}>
//                     <Text style={thStyle}>{h}</Text>
//                   </View>
//                 ))}
//               </View>
//             )}
// 
//             {/* tbody */}
//             {SK.languages.map(l => {
//               const coverage = getCoverage(l);
//               const badge = (
//                 <View
//                   style={{
//                     width: 36, height: 28, borderRadius: radii.r2, borderWidth: 1,
//                     borderColor: colors.line, backgroundColor: colors.paper2,
//                     alignItems: 'center', justifyContent: 'center',
//                   }}
//                 >
//                   <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>{l.short}</Text>
//                 </View>
//               );
//               const coverageBar = (
//                 <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
//                   <View style={{ flex: isMobile ? 1 : undefined, width: isMobile ? undefined : 120, height: 7, borderRadius: radii.full, backgroundColor: colors.paper3, overflow: 'hidden' }}>
//                     <View style={{ height: '100%', width: `${coverage}%` as any, backgroundColor: l.isDefault ? colors.success : colors.warn }} />
//                   </View>
//                   <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>{coverage}%</Text>
//                 </View>
//               );
//               const defaultCtl = l.isDefault ? (
//                 <Chip tone="accent" dot>Default</Chip>
//               ) : (
//                 <Button size="xs" kind="ghost" onPress={() => toast.success(`${l.label} set as default`)}>Set default</Button>
//               );
//               const aiBtn = (
//                 <Button kind="ghost" size="sm" iconOnly icon={<Icon name="sparkles" size={15} color={colors.ink3} />} onPress={() => toast.info('AI translating missing fields — review before publish')} />
//               );
// 
//               // ── Mobile: stacked card ──
//               if (isMobile) {
//                 return (
//                   <View key={l.code} style={{ borderBottomWidth: 1, borderBottomColor: colors.line, padding: 12, gap: 10 }}>
//                     <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
//                       {badge}
//                       <View style={{ flex: 1, minWidth: 0 }}>
//                         <Text style={{ fontWeight: fontWeight.semibold, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans }}>{l.label}</Text>
//                         <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>{l.code}</Text>
//                       </View>
//                       {aiBtn}
//                     </View>
//                     {coverageBar}
//                     <View style={{ flexDirection: 'row', alignItems: 'center' }}>{defaultCtl}</View>
//                   </View>
//                 );
//               }
// 
//               // ── Desktop: table row ──
//               return (
//                 <View key={l.code} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: colors.line, alignItems: 'center' }}>
//                   <View style={{ flex: 2, paddingVertical: 10, paddingHorizontal: 12, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
//                     {badge}
//                     <Text style={{ fontWeight: fontWeight.semibold, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans }}>{l.label}</Text>
//                   </View>
//                   <View style={{ flex: 2, paddingVertical: 10, paddingHorizontal: 12 }}>
//                     <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.sm, color: colors.ink3 }}>{l.code}</Text>
//                   </View>
//                   <View style={{ flex: 2, paddingVertical: 10, paddingHorizontal: 12 }}>{coverageBar}</View>
//                   <View style={{ flex: 2, paddingVertical: 10, paddingHorizontal: 12 }}>{defaultCtl}</View>
//                   <View style={{ width: 40, paddingVertical: 10, paddingHorizontal: 6 }}>{aiBtn}</View>
//                 </View>
//               );
//             })}
//           </View>
//         </ScrollView>
//       </View>
// 
//       <View style={{ marginTop: 12 }}>
//         <Button
//           icon={<Icon name="plus" size={15} color={colors.ink2} />}
//           onPress={() => toast.info('Add language (demo)')}
//         >
//           Add language
//         </Button>
//       </View>
//     </View>
//   );
// }
// 
// // ── Tab: Compliance ──────────────────────────────────────────
// 
// function TabCompliance() {
//   const { theme } = useTheme();
//   const { colors, fontSize, fontWeight } = theme;
//   const toast = useToast();
// 
//   return (
//     <View style={{ gap: 14 }}>
//       {/* Data protection */}
//       <Card title="Data protection (DPDP)" sub="India's Digital Personal Data Protection Act">
//         <View style={{ gap: 12 }}>
//           {/* Data residency */}
//           <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
//             <View style={{ flex: 1 }}>
//               <Text style={{ fontWeight: fontWeight.medium, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans }}>
//                 Data residency
//               </Text>
//               <Text style={{ fontSize: fontSize.sm, color: colors.ink3, marginTop: 2, fontFamily: theme.font.sans }}>
//                 Catalog data stored in India (ap-south-1)
//               </Text>
//             </View>
//             <Chip tone="success" dot>Compliant</Chip>
//           </View>
// 
//           {/* Divider */}
//           <View style={{ height: 1, backgroundColor: colors.line }} />
// 
//           {/* Audit trail */}
//           <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
//             <View style={{ flex: 1 }}>
//               <Text style={{ fontWeight: fontWeight.medium, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans }}>
//                 Audit trail
//               </Text>
//               <Text style={{ fontSize: fontSize.sm, color: colors.ink3, marginTop: 2, fontFamily: theme.font.sans }}>
//                 Immutable version history for every change
//               </Text>
//             </View>
//             <Switch checked={true} onChange={() => {}} />
//           </View>
// 
//           {/* Deactivate-only enforcement */}
//           <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
//             <View style={{ flex: 1 }}>
//               <Text style={{ fontWeight: fontWeight.medium, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans }}>
//                 Deactivate-only enforcement
//               </Text>
//               <Text style={{ fontSize: fontSize.sm, color: colors.ink3, marginTop: 2, fontFamily: theme.font.sans }}>
//                 Hard deletes blocked at the data layer — references never orphan
//               </Text>
//             </View>
//             <Chip tone="neutral" icon={<Icon name="lock" size={12} color={colors.neutral} />}>
//               Enforced
//             </Chip>
//           </View>
//         </View>
//       </Card>
// 
//       {/* Approval workflow */}
//       <Card title="Approval workflow" sub="Optional editorial sign-off — arrives in a later release">
//         <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
//           <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans, flex: 1, marginRight: 12 }}>
//             Require a second reviewer before publishing in regulated categories.
//           </Text>
//           <Chip tone="neutral" icon={<Icon name="clock" size={12} color={colors.neutral} />}>
//             Later
//           </Chip>
//         </View>
//       </Card>
// 
//       {/* Export & deletion */}
//       <Card title="Export & deletion requests">
//         <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
//           <Button
//             icon={<Icon name="download" size={15} color={colors.ink2} />}
//             onPress={() => toast.success('Tenant export queued')}
//           >
//             Export all catalog data
//           </Button>
//           <Button
//             icon={<Icon name="history" size={15} color={colors.ink2} />}
//             onPress={() => toast.info('Showing audit log')}
//           >
//             View audit log
//           </Button>
//         </View>
//       </Card>
//     </View>
//   );
// }
// 
// // ── SettingsScreen ───────────────────────────────────────────
// 
// export default function SettingsScreen() {
//   const { theme } = useTheme();
//   const { colors, fontSize, fontWeight } = theme;
// 
//   const [tab, setTab] = useState('roles');
//   const [inviteOpen, setInviteOpen] = useState(false);
// 
//   return (
//     <ScrollView
//       style={{ flex: 1, backgroundColor: colors.paper }}
//       contentContainerStyle={{ padding: 24, paddingBottom: 64 }}
//     >
//       <View style={{ maxWidth: 980, width: '100%', alignSelf: 'center' }}>
//         {/* Page header */}
//         <View style={{ marginBottom: 20 }}>
//           <Text
//             style={{
//               fontSize: fontSize['3xl'],
//               fontWeight: fontWeight.semibold,
//               letterSpacing: -0.02 * fontSize['3xl'],
//               color: colors.ink,
//               fontFamily: theme.font.sans,
//             }}
//           >
//             Settings
//           </Text>
//           <Text style={{ fontSize: fontSize.md, color: colors.ink3, marginTop: 4, fontFamily: theme.font.sans }}>
//             Team & roles, languages, and compliance for this tenant.
//           </Text>
//         </View>
// 
//         {/* Tabs */}
//         <Tabs
//           value={tab}
//           onChange={setTab}
//           tabs={[
//             { id: 'roles',      label: 'Roles & team', count: SK.users.length },
//             { id: 'languages',  label: 'Languages',    count: SK.languages.length },
//             { id: 'compliance', label: 'Compliance' },
//           ]}
//         />
// 
//         <View style={{ marginTop: 20 }}>
//           {tab === 'roles' && (
//             <TabRoles onInvite={() => setInviteOpen(true)} />
//           )}
//           {tab === 'languages' && <TabLanguages />}
//           {tab === 'compliance' && <TabCompliance />}
//         </View>
//       </View>
// 
//       <InviteMemberModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
//     </ScrollView>
//   );
// }
